/** The schedule module's reads and writes of the API: TanStack Query hooks, and a few plain calls for a form to make as it is filled in. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiDeleteWithResult, apiGet, apiGetPage, apiPost, apiPut, type WorkerRole } from '../core/index.js'
import type { AvailableShift, PublishShiftInput, BridgeAnswer, BridgeCallout, BridgePoolPreview, ClinicalBridgeStatus, DraftScheduleView, FacilityDrafts, FacilityShiftPreferences, OpenBridgeCalloutInput, SaveShiftPreferencesInput, ShiftPreferencesView, WorkerBidWindow, WorkerBridgeCallout, ScheduleRun, ScheduleRunList, SchedulePeriodView, PeriodReadinessView, ScheduleReminderSent, RemindAllResult, WorkerScheduleResultView, AssignPostingInput, ChangeRequest, Posting, RequestSwapInput, ScheduleBoardView, ScheduleCoverageView, SubmitChangeInput, SwapCandidate, SwapRequest, VacateShiftInput, WorkerBoardView, CensusEntryInput, CensusEvaluationRecord, CensusEvaluationView, CensusRequirementsRangeView, CensusRequirementsView, CensusView, HeldSeat, RecordCensusResult, StaffingReport, WorkforcePlan, MonthHealthView, ReleaseRunInput, RunAssignCandidates, AssignSeatInput, DecisionsView, RunShortInput, PostGapInput, PostingAudience, FacilityHeatmapView, FacilityHeatmapHistoryView } from './types.js'
import { draftBehindPublished } from './components/draftStaleness.js'
import { biddingStatusOf, type BiddingStatus } from './components/biddingStatus.js'
import { todayDate } from './components/scheduleCalendarData.js'

export function useWorkerBidWindow(workerId: string | null) {
  return useQuery({
    queryKey: ['worker-bid-window', workerId],
    queryFn: () => apiGet<WorkerBidWindow>(`/workers/${workerId as string}/bid-window`),
    enabled: workerId !== null,
  })
}

export function useClinicalBridge(workerId: string | null) {
  return useQuery({
    queryKey: ['clinical-bridge', workerId],
    queryFn: () => apiGet<ClinicalBridgeStatus>(`/workers/${workerId as string}/clinical-bridge`),
    enabled: workerId !== null,
  })
}

/**
 * The worker's own enrollment for Clinical Bridge days: `PUT` to say they
 * would cover a gap on a date, `DELETE` to take it back. Both answer the
 * record as the read does, which replaces the cached one at once.
 */
export function useBridgeEnrollment(workerId: string) {
  const queryClient = useQueryClient()
  const settle = (status: ClinicalBridgeStatus) => queryClient.setQueryData(['clinical-bridge', workerId], status)
  const path = (date: string) => `/workers/${encodeURIComponent(workerId)}/clinical-bridge/dates/${encodeURIComponent(date)}`
  const enroll = useMutation({ mutationFn: (date: string) => apiPut<ClinicalBridgeStatus>(path(date), undefined), onSuccess: settle })
  const withdraw = useMutation({ mutationFn: (date: string) => apiDeleteWithResult<ClinicalBridgeStatus>(path(date)), onSuccess: settle })
  return { enroll, withdraw }
}

export function useAvailableShifts(facilityId: string | null) {
  return useQuery({
    queryKey: ['available-shifts', facilityId],
    queryFn: () => apiGet<AvailableShift[]>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/available-shifts`),
    enabled: facilityId !== null,
  })
}

/** Publish shifts into the facility's pool, one part at a time: answers the seats written. Staff. */
export function usePublishShifts(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (shifts: PublishShiftInput[]) => apiPost<AvailableShift[]>(`/facilities/${encodeURIComponent(facilityId)}/schedule/shifts`, { shifts }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['available-shifts', facilityId] })
      settleRuns(queryClient, facilityId)
    },
  })
}

/** Where bidding stands for the worker (`BiddingStatus`): null while the reads it needs are loading or failed. */
export function useBiddingStatus(workerId: string | null): BiddingStatus | null {
  const preferences = useShiftPreferences(workerId)
  const shifts = useAvailableShifts(preferences.data?.facilityId ?? null)
  const draft = useDraftSchedule(workerId)
  if (!preferences.data || !shifts.data) {
    return null
  }
  return biddingStatusOf(preferences.data, shifts.data, todayDate(), draft.data?.draft ?? null)
}

// Shift preferences.

export const shiftPreferencesKey = (workerId: string | null, period: string | null) => ['shift-preferences', workerId, period] as const

/** A worker's preferences for a month (next month when none is named), with what the form offers. The worker, staff and a partner read it. */
export function useShiftPreferences(workerId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: shiftPreferencesKey(workerId, period),
    queryFn: () => apiGet<ShiftPreferencesView>(`/workers/${encodeURIComponent(workerId as string)}/shift-preferences${period ? `?period=${encodeURIComponent(period)}` : ''}`),
    enabled: workerId !== null,
  })
}

/** The worker saves their preferences; the answer replaces the cached view for that month and the default read. */
export function useSaveShiftPreferences(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveShiftPreferencesInput) => apiPut<ShiftPreferencesView>(`/workers/${encodeURIComponent(workerId)}/shift-preferences`, input),
    onSuccess: (view) => {
      queryClient.setQueryData(shiftPreferencesKey(workerId, view.period), view)
      void queryClient.invalidateQueries({ queryKey: ['shift-preferences', workerId] })
    },
  })
}

export const draftScheduleKey = (workerId: string | null, period: string | null) => ['draft-schedule', workerId, period] as const

/** The draft the engine built for the worker's month, and whether their preferences, the rules or the published shifts moved on since. */
export function useDraftSchedule(workerId: string | null, period: string | null = null) {
  const query = useQuery({
    queryKey: draftScheduleKey(workerId, period),
    queryFn: () => apiGet<DraftScheduleView>(`/workers/${encodeURIComponent(workerId as string)}/schedule/draft${period ? `?period=${encodeURIComponent(period)}` : ''}`),
    enabled: workerId !== null,
  })
  // A draft built while the month was empty is behind once shifts are published for it - said here too for an API that does not yet.
  const shifts = useAvailableShifts(query.data?.draft?.flags.includes('nothing_published') ? query.data.facilityId : null)
  const view = query.data
  if (view?.draft && !view.isStale && draftBehindPublished(view.draft, shifts.data ?? null)) {
    return { ...query, data: { ...view, isStale: true } } as typeof query
  }
  return query
}

function settleDraft(queryClient: ReturnType<typeof useQueryClient>, workerId: string, view: DraftScheduleView): void {
  queryClient.setQueryData(draftScheduleKey(workerId, view.period), view)
  queryClient.setQueryData(draftScheduleKey(workerId, null), view)
  void queryClient.invalidateQueries({ queryKey: ['draft-schedule', workerId] })
}

/** "Build my schedule": the engine drafts the month from the saved preferences; a rebuild replaces the draft. */
export function useBuildDraft(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (period: string | null) => apiPost<DraftScheduleView>(`/workers/${encodeURIComponent(workerId)}/schedule/draft`, period ? { period } : {}),
    onSuccess: (view) => settleDraft(queryClient, workerId, view),
  })
}

/** "Submit as-is": the worker accepts the draft. */
export function useAcceptDraft(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (period: string | null) => apiPost<DraftScheduleView>(`/workers/${encodeURIComponent(workerId)}/schedule/draft/accept`, period ? { period } : {}),
    onSuccess: (view) => settleDraft(queryClient, workerId, view),
  })
}

/** Every worker's draft at a facility for a month. Staff and a partner. */
export function useFacilityDrafts(facilityId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: ['facility-drafts', facilityId, period],
    queryFn: () => apiGet<FacilityDrafts>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/drafts${period ? `?period=${encodeURIComponent(period)}` : ''}`),
    enabled: facilityId !== null,
  })
}

/** Every worker's preferences at a facility for a month: what a scheduler builds from. Staff and a partner. */
export function useFacilityShiftPreferences(facilityId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: ['facility-shift-preferences', facilityId, period],
    queryFn: () => apiGet<FacilityShiftPreferences>(`/facilities/${encodeURIComponent(facilityId as string)}/shift-preferences${period ? `?period=${encodeURIComponent(period)}` : ''}`),
    enabled: facilityId !== null,
  })
}

// The facility-wide run (scheduling rules plan phase B).

const q = (period: string | null): string => (period ? `?period=${encodeURIComponent(period)}` : '')

/** Where a month stands at a facility: the timeline, the counts, the latest and released runs. Staff and a partner. */
export function useSchedulePeriod(facilityId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: ['schedule-period', facilityId, period],
    queryFn: () => apiGet<SchedulePeriodView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/period${q(period)}`),
    enabled: facilityId !== null,
  })
}

/** The month's readiness: every worker on the roster with their preferences and draft, beside the counts. Staff only. */
export function useSchedulePeriodWorkers(facilityId: string | null, period: string | null, enabled = true) {
  return useQuery({
    queryKey: ['schedule-period-workers', facilityId, period],
    queryFn: () => apiGet<PeriodReadinessView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/period/workers${q(period)}`),
    enabled: enabled && facilityId !== null,
  })
}

/** Remind: one message to a worker whose month is not done, at most once a day. Staff. */
export function useRemindWorker(facilityId: string, period: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (workerId: string) => apiPost<ScheduleReminderSent>(`/facilities/${encodeURIComponent(facilityId)}/schedule/period/workers/${encodeURIComponent(workerId)}/remind`, period ? { period } : {}),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['schedule-period-workers', facilityId] }),
  })
}

/** Remind all: everyone To remind who was not reminded within the day. Staff. */
export function useRemindAll(facilityId: string, period: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<RemindAllResult>(`/facilities/${encodeURIComponent(facilityId)}/schedule/period/remind`, period ? { period } : {}),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['schedule-period-workers', facilityId] }),
  })
}

/** The month's runs, newest first, as summaries. */
export function useScheduleRuns(facilityId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: ['schedule-runs', facilityId, period],
    queryFn: () => apiGet<ScheduleRunList>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/runs${q(period)}`),
    enabled: facilityId !== null,
  })
}

/** One run in full: every result, every contest, the unfilled shifts. */
export function useScheduleRun(facilityId: string | null, runId: string | null) {
  return useQuery({
    queryKey: ['schedule-run', facilityId, runId],
    queryFn: () => apiGet<ScheduleRun>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/runs/${encodeURIComponent(runId as string)}`),
    enabled: facilityId !== null && runId !== null,
  })
}

function settleRuns(queryClient: ReturnType<typeof useQueryClient>, facilityId: string): void {
  void queryClient.invalidateQueries({ queryKey: ['schedule-period', facilityId] })
  void queryClient.invalidateQueries({ queryKey: ['schedule-period-workers', facilityId] })
  void queryClient.invalidateQueries({ queryKey: ['schedule-runs', facilityId] })
  void queryClient.invalidateQueries({ queryKey: ['schedule-run', facilityId] })
  void queryClient.invalidateQueries({ queryKey: ['schedule-coverage', facilityId] })
  void queryClient.invalidateQueries({ queryKey: ['month-health', facilityId] })
}

/** Build the month: every worker drafted in rank order. Staff. */
export function useBuildScheduleRun(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (period: string | null) => apiPost<ScheduleRun>(`/facilities/${encodeURIComponent(facilityId)}/schedule/runs`, period ? { period } : {}),
    onSuccess: () => settleRuns(queryClient, facilityId),
  })
}

/** Release a run: every worker is told their part. Staff. */
export function useReleaseScheduleRun(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId, override }: { runId: string } & ReleaseRunInput) => apiPost<ScheduleRun>(`/facilities/${encodeURIComponent(facilityId)}/schedule/runs/${encodeURIComponent(runId)}/release`, override ? { override } : {}),
    onSuccess: () => settleRuns(queryClient, facilityId),
  })
}

/** Hold a built run from the engine's release, or let it go again (facility operations dashboard plan §5). Staff. */
export function useHoldScheduleRun(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId, hold, note }: { runId: string; hold: boolean; note?: string }) => apiPost<ScheduleRun>(`/facilities/${encodeURIComponent(facilityId)}/schedule/runs/${encodeURIComponent(runId)}/${hold ? 'hold' : 'unhold'}`, note ? { note } : {}),
    onSuccess: () => settleRuns(queryClient, facilityId),
  })
}

/** Who could take an unfilled seat of a built run, in rank order (month health plan §5). Staff. */
export function useAssignCandidates(facilityId: string, runId: string, shiftId: string | null) {
  return useQuery({
    queryKey: ['schedule-run-assign-candidates', facilityId, runId, shiftId],
    queryFn: () => apiGet<RunAssignCandidates>(`/facilities/${encodeURIComponent(facilityId)}/schedule/runs/${encodeURIComponent(runId)}/assign/candidates?shiftId=${encodeURIComponent(shiftId as string)}`),
    enabled: shiftId !== null,
  })
}

/** Put a worker on an unfilled seat before the release; a month rule passes with a reason code. Staff. */
export function useAssignSeat(facilityId: string, runId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AssignSeatInput) => apiPost<ScheduleRun>(`/facilities/${encodeURIComponent(facilityId)}/schedule/runs/${encodeURIComponent(runId)}/assign`, input),
    onSuccess: () => {
      settleRuns(queryClient, facilityId)
      void queryClient.invalidateQueries({ queryKey: ['schedule-run-assign-candidates', facilityId, runId] })
    },
  })
}

/** The month against the facility's minimum staffing (phase D): the released month as the board holds it, else the latest run. Staff and a partner. */
export function useScheduleCoverage(facilityId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: ['schedule-coverage', facilityId, period],
    queryFn: () => apiGet<ScheduleCoverageView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/coverage${q(period)}`),
    enabled: facilityId !== null,
  })
}

/**
 * The month's health (month health plan): every finding with its remedy and the verdict the release guard reads.
 * Polled, so a count recorded or a shift published in another tab shows within the minute.
 */
export function useMonthHealth(facilityId: string | null, period: string | null = null, refetchInterval: number | false = 60_000) {
  return useQuery({
    queryKey: ['month-health', facilityId, period],
    queryFn: () => apiGet<MonthHealthView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/health${q(period)}`),
    enabled: facilityId !== null,
    refetchInterval,
    // Stepping to another month keeps the last one on screen until the next arrives.
    placeholderData: keepPreviousData,
  })
}

/** The worker's part of the released month, or none yet. The worker's own, staff and a partner. */
export function useWorkerScheduleResult(workerId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: ['schedule-result', workerId, period],
    queryFn: () => apiGet<WorkerScheduleResultView>(`/workers/${encodeURIComponent(workerId as string)}/schedule/result${q(period)}`),
    enabled: workerId !== null,
  })
}

// The schedule board (scheduling rules plan phase C). Asked again every half minute while on screen: every read moves the board to now.

const BOARD_REFRESH_MS = 30_000

/** The board as staff read it. 409 until the month is released, which the card shows as such. */
/** The two-minute demo (open shift lifecycle plan section 11): a seat played on a demo client, and taken down. */
export function useDemoSeat(facilityId: string, period: string | null = null) {
  const queryClient = useQueryClient()
  const onSuccess = () => settleBoard(queryClient)
  const base = `/facilities/${encodeURIComponent(facilityId)}/schedule/board/demo-seat`
  return {
    play: useMutation({ mutationFn: (input: { unitId: string; role: WorkerRole; profile?: Record<string, string | null> }) => apiPost<{ posting: Posting; period: string }>(base, input), onSuccess }),
    remove: useMutation({ mutationFn: (postingId: string) => apiDeleteWithResult<ScheduleBoardView>(`${base}/${encodeURIComponent(postingId)}${q(period)}`), onSuccess }),
  }
}

/** While a demo seat is live the Today tab polls every 5 seconds (plan section 11.3), so the stages are exact to the poll. */
export const DEMO_REFRESH_MS = 5_000

export function useScheduleBoard(facilityId: string | null, period: string | null = null, refetchInterval: number | false = BOARD_REFRESH_MS) {
  return useQuery({
    queryKey: ['schedule-board', facilityId, period],
    queryFn: () => apiGet<ScheduleBoardView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/board${q(period)}`),
    enabled: facilityId !== null,
    refetchInterval,
    retry: false,
  })
}

/**
 * The board as the worker sees it. Asked only once the month is released - the board opens with the release, and
 * before it the API answers 409 - so a month still being bid on is not asked about every half minute.
 */
export function useWorkerBoard(workerId: string | null, period: string | null = null) {
  const released = (useWorkerScheduleResult(workerId, period).data?.result ?? null) !== null
  return useQuery({
    queryKey: ['worker-board', workerId, period],
    queryFn: () => apiGet<WorkerBoardView>(`/workers/${encodeURIComponent(workerId as string)}/schedule/board${q(period)}`),
    enabled: workerId !== null && released,
    refetchInterval: BOARD_REFRESH_MS,
    retry: false,
  })
}

function settleBoard(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['schedule-board'] })
  void queryClient.invalidateQueries({ queryKey: ['schedule-decisions'] })
  void queryClient.invalidateQueries({ queryKey: ['posting-audience'] })
  void queryClient.invalidateQueries({ queryKey: ['worker-board'] })
  void queryClient.invalidateQueries({ queryKey: ['schedule-result'] })
  void queryClient.invalidateQueries({ queryKey: ['schedule-coverage'] })
  void queryClient.invalidateQueries({ queryKey: ['worker-notifications'] })
}

/** The staff actions on the board: close the window, vacate a shift, assign a posting by override, decide a swap. */
export function useBoardActions(facilityId: string, period: string | null = null) {
  const queryClient = useQueryClient()
  const base = `/facilities/${encodeURIComponent(facilityId)}/schedule/board`
  const onSuccess = () => settleBoard(queryClient)
  return {
    closeWindow: useMutation({ mutationFn: () => apiPost<ScheduleBoardView>(`${base}/close-window${q(period)}`, {}), onSuccess }),
    vacate: useMutation({ mutationFn: (input: VacateShiftInput) => apiPost<ScheduleBoardView>(`${base}/vacate${q(period)}`, input), onSuccess }),
    assign: useMutation({ mutationFn: ({ postingId, ...input }: AssignPostingInput & { postingId: string }) => apiPost<ScheduleBoardView>(`${base}/postings/${encodeURIComponent(postingId)}/assign${q(period)}`, input), onSuccess }),
    decideSwap: useMutation({ mutationFn: ({ swapId, decision }: { swapId: string; decision: 'approve' | 'deny' }) => apiPost<SwapRequest>(`${base}/swaps/${encodeURIComponent(swapId)}/${decision}${q(period)}`, {}), onSuccess }),
    runShort: useMutation({ mutationFn: ({ postingId, ...input }: RunShortInput & { postingId: string }) => apiPost<ScheduleBoardView>(`${base}/postings/${encodeURIComponent(postingId)}/run-short${q(period)}`, input), onSuccess }),
    /** An uncovered gap's seats onto the board (26 October 2026); the month is the date's. */
    postGap: useMutation({ mutationFn: (input: PostGapInput) => apiPost<{ posted: number; board: ScheduleBoardView }>(`${base}/gaps`, input), onSuccess }),
  }
}

/**
 * Who was told about one open seat and how it reached them (who was told design, 26 October 2026): read only while its
 * row is open, and asked again with the board, since a read moves the board to now.
 */
export function usePostingAudience(facilityId: string, period: string, postingId: string | null) {
  return useQuery({
    queryKey: ['posting-audience', facilityId, period, postingId],
    queryFn: () => apiGet<PostingAudience>(`/facilities/${encodeURIComponent(facilityId)}/schedule/board/postings/${encodeURIComponent(postingId as string)}/audience${q(period)}`),
    enabled: postingId !== null,
    refetchInterval: BOARD_REFRESH_MS,
  })
}

/**
 * What needs a person at the facility (facility operations dashboard plan §4): the Today tab's "Needs you". Asked
 * again every half minute, like the board it reads - a read moves the board to now.
 */
export function useDecisions(facilityId: string | null, refetchInterval: number | false = BOARD_REFRESH_MS) {
  return useQuery({
    queryKey: ['schedule-decisions', facilityId],
    queryFn: () => apiGet<DecisionsView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/decisions`),
    enabled: facilityId !== null,
    refetchInterval,
  })
}

/**
 * The facility's state as a heatmap for one month (this month when `period` is null): every day and slot judged on
 * one scale. Refreshed with the board, since today's cells move as seats are taken.
 */
export function useFacilityHeatmap(facilityId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: ['schedule-heatmap', facilityId, period],
    queryFn: () => apiGet<FacilityHeatmapView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/heatmap${period ? `?period=${encodeURIComponent(period)}` : ''}`),
    enabled: facilityId !== null,
    placeholderData: keepPreviousData,
    refetchInterval: BOARD_REFRESH_MS,
  })
}

/** The months ending this month, each day's state alone: the Month tab's history strip. */
export function useFacilityHeatmapHistory(facilityId: string | null, months = 12) {
  return useQuery({
    queryKey: ['schedule-heatmap-history', facilityId, months],
    queryFn: () => apiGet<FacilityHeatmapHistoryView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/heatmap/history?months=${months}`),
    enabled: facilityId !== null,
    staleTime: 5 * 60_000,
  })
}

/** The worker's actions: a change-window request and its answer, a swap and its answer or cancel, a posting's answer. */
export function useWorkerBoardActions(workerId: string, period: string | null = null) {
  const queryClient = useQueryClient()
  const base = `/workers/${encodeURIComponent(workerId)}/schedule`
  const onSuccess = () => settleBoard(queryClient)
  return {
    submitChange: useMutation({ mutationFn: (input: SubmitChangeInput) => apiPost<ChangeRequest>(`${base}/changes${q(period)}`, input), onSuccess }),
    answerChange: useMutation({ mutationFn: ({ requestId, answer }: { requestId: string; answer: 'accept' | 'decline' }) => apiPost<ChangeRequest>(`${base}/changes/${encodeURIComponent(requestId)}/answer${q(period)}`, { answer }), onSuccess }),
    requestSwap: useMutation({ mutationFn: (input: RequestSwapInput) => apiPost<SwapRequest>(`${base}/swaps${q(period)}`, input), onSuccess }),
    answerSwap: useMutation({ mutationFn: ({ swapId, answer }: { swapId: string; answer: 'accept' | 'decline' }) => apiPost<SwapRequest>(`${base}/swaps/${encodeURIComponent(swapId)}/answer${q(period)}`, { answer }), onSuccess }),
    cancelSwap: useMutation({ mutationFn: (swapId: string) => apiPost<SwapRequest>(`${base}/swaps/${encodeURIComponent(swapId)}/cancel${q(period)}`, {}), onSuccess }),
    answerPosting: useMutation({ mutationFn: ({ postingId, answer }: { postingId: string; answer: 'accept' | 'decline' }) => apiPost<Posting>(`${base}/board/postings/${encodeURIComponent(postingId)}/answer${q(period)}`, { answer }), onSuccess }),
  }
}

/** The ranked peers for a swap of one shift. */
export function useSwapCandidates(workerId: string, shiftId: string | null, period: string | null = null) {
  return useQuery({
    queryKey: ['swap-candidates', workerId, shiftId, period],
    queryFn: () => apiGet<SwapCandidate[]>(`/workers/${encodeURIComponent(workerId)}/schedule/swaps/candidates?shiftId=${encodeURIComponent(shiftId as string)}${period ? `&period=${encodeURIComponent(period)}` : ''}`),
    enabled: shiftId !== null,
  })
}

// Clinical Bridge callouts (docs/plans/clinical-bridge-plan.md section 7).

/** Asked again every half minute while the facility is on screen, so an answer or an expiry shows without a reload. */
const CALLOUTS_REFRESH_MS = 30_000
/** The dashboard card shows the first page only: a facility has a few callouts a week, kept 90 days. */
const CALLOUTS_PAGE_SIZE = 50

export const bridgeCalloutsKey = (facilityId: string | null) => ['bridge-callouts', facilityId] as const

/** A facility's callouts, newest first, with the status a reader sees now. Staff and partners alike. */
export function useBridgeCallouts(facilityId: string | null) {
  return useQuery({
    queryKey: bridgeCalloutsKey(facilityId),
    queryFn: async () => (await apiGetPage<BridgeCallout>(`/facilities/${encodeURIComponent(facilityId as string)}/bridge/callouts?limit=${CALLOUTS_PAGE_SIZE}`, null)).items,
    enabled: facilityId !== null,
    refetchInterval: CALLOUTS_REFRESH_MS,
  })
}

export const workerBridgeCalloutsKey = (workerId: string | null) => ['worker-bridge-callouts', workerId] as const

/**
 * The callouts the worker was asked about, newest first, as they see them -
 * asked again every half minute while the Home screen is open (plan section
 * 7), so a callout opened while the app is in hand shows without a reload
 * and a shift covered by someone else stops being offered.
 */
export function useWorkerBridgeCallouts(workerId: string | null) {
  return useQuery({
    queryKey: workerBridgeCalloutsKey(workerId),
    queryFn: () => apiGet<WorkerBridgeCallout[]>(`/workers/${encodeURIComponent(workerId as string)}/bridge/callouts`),
    enabled: workerId !== null,
    refetchInterval: CALLOUTS_REFRESH_MS,
    refetchOnWindowFocus: true,
  })
}

/** The worker answers a callout; the answer replaces the cached callout at once, and the messages refetch since the fill writes some. */
export function useRespondToBridgeCallout(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ calloutId, answer }: { calloutId: string; answer: BridgeAnswer }) => apiPost<WorkerBridgeCallout>(`/workers/${encodeURIComponent(workerId)}/bridge/callouts/${encodeURIComponent(calloutId)}/respond`, { answer }),
    onSuccess: (callout) => {
      queryClient.setQueryData<WorkerBridgeCallout[]>(workerBridgeCalloutsKey(workerId), (current) => (current ?? []).map((entry) => (entry.calloutId === callout.calloutId ? callout : entry)))
      void queryClient.invalidateQueries({ queryKey: ['worker-notifications', workerId] })
    },
    // A 409 means the callout changed under the tap: the list is read again so the card says what happened.
    onError: () => void queryClient.invalidateQueries({ queryKey: workerBridgeCalloutsKey(workerId) }),
  })
}

/** What the open-callout form asks the pool for, once its fields name a shift. */
export interface BridgePoolRequest {
  date: string
  start: string
  end: string
  role: string
  absentWorkerId: string | null
  holdBack: readonly string[]
}

/** Who a callout for the shift would go to, for the facility administrator to look at before opening. Idle until the form names a shift. */
export function useBridgePoolPreview(facilityId: string, request: BridgePoolRequest | null) {
  const params = request
    ? new URLSearchParams({ date: request.date, start: request.start, end: request.end, role: request.role, ...(request.absentWorkerId ? { absentWorkerId: request.absentWorkerId } : {}), ...(request.holdBack.length > 0 ? { holdBack: request.holdBack.join(',') } : {}) })
    : null
  return useQuery({
    queryKey: ['bridge-pool', facilityId, params?.toString() ?? null],
    queryFn: () => apiGet<BridgePoolPreview>(`/facilities/${encodeURIComponent(facilityId)}/bridge/callouts/pool?${params?.toString() ?? ''}`),
    enabled: params !== null,
    placeholderData: keepPreviousData,
  })
}

/** A facility administrator opens a callout; the list refetches. */
export function useOpenBridgeCallout(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: OpenBridgeCalloutInput) => apiPost<BridgeCallout>(`/facilities/${encodeURIComponent(facilityId)}/bridge/callouts`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bridgeCalloutsKey(facilityId) })
      void queryClient.invalidateQueries({ queryKey: ['facility-activity'] })
      // A callout opened for a seat marks it on the board's next read.
      void queryClient.invalidateQueries({ queryKey: ['schedule-board', facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['posting-audience', facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['schedule-decisions', facilityId] })
    },
  })
}

/** A facility administrator withdraws an open callout; the list refetches. */
export function useCancelBridgeCallout(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (calloutId: string) => apiPost<BridgeCallout>(`/facilities/${encodeURIComponent(facilityId)}/bridge/callouts/${encodeURIComponent(calloutId)}/cancel`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: bridgeCalloutsKey(facilityId) }),
  })
}

// --- The census (census response plan F0).

const censusKey = (facilityId: string | null) => ['census', facilityId] as const

/** `GET /facilities/{facilityId}/census?from&to`: every date of the range for every unit, recorded or forecast. Staff. */
export function useCensus(facilityId: string | null, range: { from: string; to: string } | null) {
  return useQuery({
    queryKey: [...censusKey(facilityId), range?.from ?? null, range?.to ?? null],
    queryFn: () => apiGet<CensusView>(`/facilities/${encodeURIComponent(facilityId as string)}/census?from=${encodeURIComponent(range!.from)}&to=${encodeURIComponent(range!.to)}`),
    enabled: facilityId !== null && range !== null,
    placeholderData: keepPreviousData,
  })
}

/** `PUT /facilities/{facilityId}/census`: records counts, each replacing the unit and date's; every census read of the facility is refreshed. */
export function useRecordCensus(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entries: CensusEntryInput[]) => apiPut<RecordCensusResult>(`/facilities/${encodeURIComponent(facilityId)}/census`, { entries }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: censusKey(facilityId) })
      void queryClient.invalidateQueries({ queryKey: ['census-requirements', facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['census-requirements-range', facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['census-evaluation', facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['census-evaluations', facilityId] })
      // The Month card and the facility warnings read the census too.
      void queryClient.invalidateQueries({ queryKey: ['month-health', facilityId] })
      settleBoard(queryClient)
    },
  })
}

/** `GET /facilities/{facilityId}/census/requirements?date&unitId&census`: what the date requires under the staffing plan; `census` tries a count instead of the recorded one (the What-if). */
export function useCensusRequirements(facilityId: string | null, query: { date: string; unitId?: string; census?: number } | null) {
  const search = query ? new URLSearchParams({ date: query.date, ...(query.unitId ? { unitId: query.unitId } : {}), ...(query.census !== undefined ? { census: String(query.census) } : {}) }).toString() : ''
  return useQuery({
    queryKey: ['census-requirements', facilityId, query?.date ?? null, query?.unitId ?? null, query?.census ?? null],
    queryFn: () => apiGet<CensusRequirementsView>(`/facilities/${encodeURIComponent(facilityId as string)}/census/requirements?${search}`),
    enabled: facilityId !== null && query !== null,
    placeholderData: keepPreviousData,
  })
}

/** `GET /facilities/{facilityId}/census/requirements/range?from&to`: what each day of a range requires at its census, in one read (the Today tab's week ahead). */
export function useCensusRequirementsRange(facilityId: string | null, range: { from: string; to: string } | null) {
  return useQuery({
    queryKey: ['census-requirements-range', facilityId, range?.from ?? null, range?.to ?? null],
    queryFn: () => apiGet<CensusRequirementsRangeView>(`/facilities/${encodeURIComponent(facilityId as string)}/census/requirements/range?${new URLSearchParams({ from: (range as { from: string }).from, to: (range as { to: string }).to }).toString()}`),
    enabled: facilityId !== null && range !== null,
    placeholderData: keepPreviousData,
  })
}

/** `GET /facilities/{facilityId}/schedule/census/evaluation?date&unitId&census`: what a census would change on the released month - who is flexed or floated, what posts, where the minimum holds. Staff; nothing is written. */
export function useCensusEvaluation(facilityId: string | null, query: { date: string; unitId: string; census?: number } | null) {
  const search = query ? new URLSearchParams({ date: query.date, unitId: query.unitId, ...(query.census !== undefined ? { census: String(query.census) } : {}) }).toString() : ''
  return useQuery({
    queryKey: ['census-evaluation', facilityId, query?.date ?? null, query?.unitId ?? null, query?.census ?? null],
    queryFn: () => apiGet<CensusEvaluationView>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/census/evaluation?${search}`),
    enabled: facilityId !== null && query !== null,
    placeholderData: keepPreviousData,
  })
}

/** `GET /facilities/{facilityId}/schedule/census/evaluations?period=`: the evaluations the engine made as the census was recorded, newest first. Staff. */
export function useCensusEvaluations(facilityId: string | null, period: string | null) {
  return useQuery({
    queryKey: ['census-evaluations', facilityId, period],
    queryFn: () => apiGet<CensusEvaluationRecord[]>(`/facilities/${encodeURIComponent(facilityId as string)}/schedule/census/evaluations${period ? `?period=${encodeURIComponent(period)}` : ''}`),
    enabled: facilityId !== null,
    refetchInterval: 30_000,
  })
}

/** Staff's decisions on the census response (F2): apply or dismiss a proposed evaluation, offer a held seat back. */
export function useCensusDecisions(facilityId: string) {
  const queryClient = useQueryClient()
  const base = `/facilities/${encodeURIComponent(facilityId)}/schedule/census`
  const onSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: ['census-evaluations', facilityId] })
    void queryClient.invalidateQueries({ queryKey: ['census-evaluation', facilityId] })
    settleBoard(queryClient)
  }
  return {
    apply: useMutation({ mutationFn: (evaluationId: string) => apiPost<CensusEvaluationRecord>(`${base}/evaluations/${encodeURIComponent(evaluationId)}/apply`, {}), onSuccess }),
    dismiss: useMutation({ mutationFn: (evaluationId: string) => apiPost<CensusEvaluationRecord>(`${base}/evaluations/${encodeURIComponent(evaluationId)}/dismiss`, {}), onSuccess }),
    offerRecall: useMutation({ mutationFn: (shiftId: string) => apiPost<HeldSeat>(`${base}/held/${encodeURIComponent(shiftId)}/recall`, {}), onSuccess }),
  }
}

/** The worker's side of the census response (F2): answer a recall, volunteer for a low-census day or take it back. */
export function useWorkerCensusActions(workerId: string) {
  const queryClient = useQueryClient()
  const base = `/workers/${encodeURIComponent(workerId)}/schedule`
  const onSuccess = () => settleBoard(queryClient)
  return {
    answerRecall: useMutation({ mutationFn: ({ shiftId, answer }: { shiftId: string; answer: 'accept' | 'decline' }) => apiPost<HeldSeat>(`${base}/recalls/${encodeURIComponent(shiftId)}/answer`, { answer }), onSuccess }),
    volunteer: useMutation({ mutationFn: (date: string) => apiPut<{ workerId: string; volunteerDates: string[] }>(`${base}/flex-volunteer/${encodeURIComponent(date)}`, undefined), onSuccess }),
    withdrawVolunteer: useMutation({ mutationFn: (date: string) => apiDeleteWithResult<{ workerId: string; volunteerDates: string[] }>(`${base}/flex-volunteer/${encodeURIComponent(date)}`), onSuccess }),
  }
}

/** `GET /facilities/{facilityId}/reports/workforce`: FTEs needed against the roster, hires and the master pattern. Staff. */
export function useWorkforcePlan(facilityId: string) {
  return useQuery({
    queryKey: ['workforce-plan', facilityId],
    queryFn: () => apiGet<WorkforcePlan>(`/facilities/${encodeURIComponent(facilityId)}/reports/workforce`),
  })
}

/** `GET /facilities/{facilityId}/reports/staffing?months=`: PPD and HPPD by unit and day, scheduled against worked. Staff. */
export function useStaffingReport(facilityId: string, months: number) {
  return useQuery({
    queryKey: ['staffing-report', facilityId, months],
    queryFn: () => apiGet<StaffingReport>(`/facilities/${encodeURIComponent(facilityId)}/reports/staffing?months=${months}`),
    placeholderData: keepPreviousData,
  })
}
